import {
  BRANCH_HEADER,
  CLINIC_HEADER,
  MODULE_REQUIRED,
  PERMISSION_REQUIRED,
} from "@dentacore/shared-types";
import type {
  AuthUser,
  InviteSubject,
  RefreshedTokens,
  SignupInput,
  SignupResult,
  SlugAvailability,
  TokenPair,
} from "@dentacore/shared-types";

import { announceModulesChanged } from "../shell/module-events";
import { announceAccessChanged } from "../auth/access-events";
import { locatedBranch } from "./located";
import { activeBranch, tokens, SESSION_IN_COOKIE } from "./tokens";

export const API_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:3000";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * A machine-readable reason, when the server sends one.
     *
     * Only a handful of failures have one, and they are the failures the client
     * has to *do* something about rather than merely display — matching on the
     * message text instead would break the next time somebody rewords it.
     */
    readonly code?: string,
    /**
     * The rest of the refusal's body, when it carried one.
     *
     * **A code says which refusal; this says what it was about.** Two of them
     * now need more than their own name to be acted on: `PHONE_ALREADY_USED`
     * carries the patient who already has the number, and
     * `PLAN_VISIT_OUT_OF_ORDER` carries the visit standing in the way. Both feed
     * a dialog that offers the one action which resolves the refusal, and
     * neither can be built by parsing the sentence back out of `message`.
     *
     * Deliberately untyped here. `ApiError` is thrown by every call in the app
     * and has no business knowing the shape of any one of them; the handful of
     * call sites that read this narrow it themselves.
     */
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Raised when the session is over and the user has to sign in again. */
export class SessionExpiredError extends ApiError {
  constructor() {
    super(401, "Your session has ended. Please sign in again.");
    this.name = "SessionExpiredError";
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /**
   * Marks a branch-scoped route. **No longer changes what is sent** — the active
   * location goes with every call since roles became per location (R-D6) — and
   * kept so the call sites still say which routes need one.
   */
  branchScoped?: boolean;
  /**
   * **This location instead of the active one**, for one read that walks a
   * person's locations — My week on Me (HR-D19). The server still checks the
   * person works there, so naming another location widens nothing.
   */
  branchId?: string;
  signal?: AbortSignal;
}

/**
 * One refresh at a time, shared by everyone waiting for it.
 *
 * The case this exists for: a screen fires five requests at once, the access
 * token has just expired, and all five come back 401. Refreshing per request
 * would send five refreshes with the same token — and the server treats the
 * second one as a **replayed** token, which is not a harmless duplicate. It is
 * the signal that a token leaked, so the server revokes every session the user
 * holds and logs them out. Concurrency in the client would look exactly like
 * theft to the server.
 *
 * So the first 401 starts a refresh and stores the promise; the other four
 * await that same promise, then retry with the token it produced.
 */
let refreshInFlight: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = tokens.getRefreshToken();
  if (refreshToken === null) throw new SessionExpiredError();

  /**
   * **The cookie carries the token** (OQ-D22); the header proves this page
   * meant the refresh. A browser still holding a token from before the cookie
   * sends it once in the body, and the answer moves it into the cookie.
   */
  const response = await fetch(`${API_URL}/auth/refresh`, {
    credentials: "include",
    method: "POST",
    headers: { "content-type": "application/json", "x-dentacore-refresh": "1" },
    body: JSON.stringify(refreshToken === SESSION_IN_COOKIE ? {} : { refreshToken }),
  });

  if (!response.ok) {
    // Expired, revoked, or detected as replayed. All three mean the same thing
    // to a client: this session is over.
    tokens.clear();
    throw new SessionExpiredError();
  }

  const refreshed = (await response.json()) as RefreshedTokens;
  tokens.set({ accessToken: refreshed.accessToken });
  return refreshed.accessToken;
}

function coordinatedRefresh(): Promise<string> {
  refreshInFlight ??= refreshAccessToken().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/**
 * The same shared refresh, for a caller that talks to the API without `api()` —
 * the live stream, which reads a response body as it arrives. Rejects with
 * `SessionExpiredError` when the session is over.
 */
export function refreshSession(): Promise<string> {
  return coordinatedRefresh();
}

async function send(
  path: string,
  options: RequestOptions,
  accessToken: string | null,
): Promise<Response> {
  const headers: Record<string, string> = {};

  if (options.body !== undefined) headers["content-type"] = "application/json";
  if (accessToken !== null) headers.authorization = `Bearer ${accessToken}`;

  // **The active location goes with every call, not only branch-scoped ones**
  // (R-D6, QA 2026-10-04). Roles are given per location, and a clinic-wide call
  // — a patient's file, their notes, their invoices, `/auth/me` itself — is
  // checked against the roles held where the person is working. Left off, the
  // server fell back to everything they hold anywhere. It decides nothing else
  // on a clinic-wide route, and the server uses it only if they work there.
  // Sent only when there is one: a missing header on a branch-scoped route is
  // a clear 400, an empty one a confusing 403.
  const branchId = options.branchId ?? activeBranch.get();
  if (branchId !== null) headers[BRANCH_HEADER] = branchId;

  return fetch(`${API_URL}${path}`, {
    credentials: "include",
    method: options.method ?? "GET",
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });
}

/**
 * Every call the application makes goes through here.
 *
 * On a 401 it refreshes once and retries the request exactly once. It does not
 * retry a second time: if a freshly minted access token is also rejected, the
 * problem is not staleness and looping would only turn it into a hot one.
 */
export async function api<T>(path: string, given: RequestOptions = {}): Promise<T> {
  // The location is settled **now, before the first await**, so a call made
  // inside `atLocation` keeps that location for its retry after a refresh too.
  const branchId = given.branchId ?? scopedBranch ?? locatedBranch(path) ?? undefined;
  const options: RequestOptions = branchId === undefined ? given : { ...given, branchId };

  let response = await send(path, options, tokens.getAccessToken());

  if (response.status === 401 && tokens.getRefreshToken() !== null) {
    const accessToken = await coordinatedRefresh();
    response = await send(path, options, accessToken);
  }

  if (response.status === 401) {
    tokens.clear();
    throw new SessionExpiredError();
  }

  if (!response.ok) await fail(response);

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** Set only while `atLocation` is running its call — see below. */
let scopedBranch: string | null = null;

/**
 * **Send one call to a booking's own location** (CF-D15): with several
 * locations in one calendar, every action on a booking goes to the location it
 * belongs to, whatever the active location is.
 *
 * The call must start its request synchronously — every wrapper in `api/` does,
 * its first statement being `api(...)` — because the location is read when
 * `api` is entered and the scope ends as soon as `call` returns. `null` leaves
 * the active location in charge, so a single-location calendar passes through
 * unchanged. The server still checks the person may act there.
 */
export function atLocation<T>(branchId: string | null, call: () => T): T {
  if (branchId === null) return call();
  const previous = scopedBranch;
  scopedBranch = branchId;
  try {
    return call();
  } finally {
    scopedBranch = previous;
  }
}

/**
 * Read a failed response and throw it as an `ApiError` **carrying its code**.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 *
 * `ApiError` gained a `code` so the client could act on a refusal rather than
 * only print it — reveal the practice field, offer the shared-number tick. Only
 * the login call was updated to pass one, and every other call site kept
 * `new ApiError(status, await readErrorMessage(response))`.
 *
 * So `code` was `undefined` everywhere except sign-in, and the one feature
 * built on it did nothing: the server correctly returned
 * `PHONE_ALREADY_USED` with a message telling the reader to tick a box, and the
 * box never appeared. **Found by walking the form**, not by a test — the server
 * spec asserted the code and the client never read it.
 *
 * One thrower, so a new call site cannot repeat it.
 */
async function fail(response: Response): Promise<never> {
  const { message, code, details } = await readError(response);
  // A module the tab thought the clinic had is gone (or never was): have the
  // shell read the modules again rather than keep drawing the old ones (QA-13).
  if (response.status === 403 && code === MODULE_REQUIRED) announceModulesChanged();
  // Likewise a permission it thought this person held (a role changed): read them again.
  if (response.status === 403 && code === PERMISSION_REQUIRED) announceAccessChanged();
  throw new ApiError(response.status, message, code, details);
}

/** The message, the code when the server sent one, and the rest of the body. */
async function readError(
  response: Response,
): Promise<{ message: string; code?: string; details?: Record<string, unknown> }> {
  try {
    const body = (await response.json()) as { message?: string | string[]; code?: string };
    const code = typeof body.code === "string" ? body.code : undefined;
    // The whole body, so a refusal that carries structure — which patient
    // already has the number, which visit is outstanding — reaches the screen
    // that has to offer a way out of it.
    const details = body as Record<string, unknown>;
    if (Array.isArray(body.message)) return { message: body.message.join(", "), code, details };
    if (typeof body.message === "string") return { message: body.message, code, details };
  } catch {
    // Not JSON. Fall through to the status text.
  }
  return { message: response.statusText || `Request failed with ${String(response.status)}` };
}

/**
 * The clinic this browser is pointed at, from its own hostname.
 *
 * `cedar.dentacore.app` and `cedar.localhost:5173` both give `cedar` — browsers
 * resolve any `*.localhost` to the loopback address, so subdomain routing can be
 * developed and tested without touching `/etc/hosts`.
 *
 * A bare `localhost`, an IP address or an apex domain gives nothing, and the API
 * then resolves the tenant only if there is exactly one clinic. That is what
 * keeps `pnpm dev` and the e2e suite working against a single seeded clinic
 * while a real deployment, which always has several, must carry a subdomain.
 */
function clinicSlug(): string | undefined {
  const { hostname } = window.location;

  if (/^\d+(\.\d+)*$/.test(hostname)) return undefined;

  // Three labels, or two when the last is `localhost`. Two labels otherwise is
  // an apex domain — `dentacore.app` is the product, not a customer — and
  // reading `dentacore` as a slug would sign the marketing site in to whichever
  // clinic happened to hold it. Same rule as the API's `clinicSlugOf`.
  const labels = hostname.split(".");
  const isLoopbackName = labels.at(-1) === "localhost";
  if (labels.length < (isLoopbackName ? 2 : 3)) return undefined;

  const first = labels[0];
  if (first === undefined || first.length === 0 || first === "www") return undefined;

  return first.toLowerCase();
}

// ── the calls the skeleton needs ────────────────────────────────────────────

export async function login(
  email: string,
  password: string,
  /**
   * The practice, when the address bar does not name one.
   *
   * Typed by the person signing in, and only ever asked for after the server
   * has said it cannot tell which practice is meant. A subdomain always wins:
   * somebody at `cedar.dentacore.com` is signing in to Cedar, and a stale
   * value in a form field must not be able to say otherwise.
   */
  practice?: string,
): Promise<AuthUser> {
  // Deliberately not through `api()`: there is no token yet, and a 401 here
  // means "wrong password", not "refresh and try again".
  const headers: Record<string, string> = { "content-type": "application/json" };

  // Which clinic this browser is signing in to, taken from the address bar.
  // The API is a different origin, so its own Host names the API rather than
  // the tenant — the browser is the only party that knows which subdomain the
  // user actually opened.
  const slug = clinicSlug() ?? normalisePractice(practice);
  if (slug !== undefined) headers[CLINIC_HEADER] = slug;

  const response = await fetch(`${API_URL}/auth/login`, {
    credentials: "include",
    method: "POST",
    headers,
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) await fail(response);

  const pair = (await response.json()) as TokenPair;
  tokens.set(pair);
  return pair.user;
}

/**
 * What somebody types into the practice field, reduced to a slug.
 *
 * People paste the whole address — `cedar.dentacore.com`, or the URL — as
 * readily as they type `cedar`, and refusing the forms that are not the slug
 * would be a puzzle rather than a question. Taking the first label of whatever
 * arrives handles all of them, and an empty field stays undefined so the server
 * answers the same way it would have anyway.
 */
function normalisePractice(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;

  // Split on "//" rather than a regex: a scheme is the only thing that puts one
  // in an address, and the last segment is the host either way.
  const trimmed = value.trim().toLowerCase().split("//").at(-1) ?? "";
  const first = trimmed.split(/[./:]/)[0] ?? "";
  return first.length === 0 ? undefined : first;
}

/**
 * Who an invite link is for, so the accept page can greet them.
 *
 * **Not through `api()`**, for the same reason `login` is not: there is no
 * token, and a 404 here means "this link is no good", not "refresh and retry".
 * The clinic travels as the subdomain header exactly as it does at login — the
 * API is a different origin, so the browser is the only party that knows which
 * subdomain was actually opened.
 */
export async function inviteSubject(token: string): Promise<InviteSubject> {
  const headers: Record<string, string> = {};
  const slug = clinicSlug();
  if (slug !== undefined) headers[CLINIC_HEADER] = slug;

  const response = await fetch(`${API_URL}/auth/invite/${encodeURIComponent(token)}`, {
    headers,
    credentials: "include",
  });
  if (!response.ok) await fail(response);

  return (await response.json()) as InviteSubject;
}

/**
 * Spend the link: set a password and land signed in.
 *
 * The response is a full token pair, so this ends with the same `tokens.set`
 * login does — the person is signed in when it resolves, which is what
 * STORY-064's acceptance check asks for.
 */
export async function acceptInvite(
  token: string,
  /**
   * Absent when this person already has an account (I2) — they are joining a
   * second practice, not registering, and keep the password they have.
   */
  password: string | undefined,
): Promise<AuthUser> {
  const headers: Record<string, string> = { "content-type": "application/json" };
  const slug = clinicSlug();
  if (slug !== undefined) headers[CLINIC_HEADER] = slug;

  const response = await fetch(`${API_URL}/auth/invite/${encodeURIComponent(token)}/accept`, {
    credentials: "include",
    method: "POST",
    headers,
    // Omitted rather than sent empty: the server tells an absent password from
    // one that fails the policy, and they mean different things.
    body: JSON.stringify(password === undefined ? {} : { password }),
  });

  if (!response.ok) await fail(response);

  const pair = (await response.json()) as TokenPair;
  tokens.set(pair);
  return pair.user;
}

// ── self-serve sign-up (2026-09-05) ─────────────────────────────────────────
//
// **None of these carries the clinic header, and that is the point.** They run
// at the apex, before any practice exists — there is nothing to scope them to.
// `handoff` is the exception and the reason it is a separate call: by the time
// it runs the browser has already been moved to the new subdomain, so
// `clinicSlug()` finally has an answer.

/** Whether a subdomain is free, asked while somebody is still typing. */
export async function checkSlug(slug: string, signal?: AbortSignal): Promise<SlugAvailability> {
  const response = await fetch(`${API_URL}/signup/availability?slug=${encodeURIComponent(slug)}`, {
    credentials: "include",
    signal,
  });
  if (!response.ok) await fail(response);

  return (await response.json()) as SlugAvailability;
}

/**
 * Create the practice.
 *
 * Returns where it lives and a five-minute hand-off, **not a session** — the
 * caller has to move the browser to `result.origin` before a session can exist,
 * because the refresh token lives in `localStorage` and that is per-origin.
 */
export async function signUp(input: SignupInput): Promise<SignupResult> {
  const response = await fetch(`${API_URL}/signup`, {
    credentials: "include",
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });

  if (!response.ok) await fail(response);

  return (await response.json()) as SignupResult;
}

/** Spend the hand-off, at the practice's own origin, for a real session. */
export async function spendHandoff(handoff: string): Promise<AuthUser> {
  return spendAt("/auth/handoff", handoff);
}

/**
 * Spend a **chooser** hand-off, at the practice the person picked.
 *
 * Same exchange as `spendHandoff` and a different route, because the two
 * tokens have different lives and different spend rules — see
 * `clinic-choice-handoff.ts` on the API side. Sharing the transport and not
 * the endpoint is what keeps that true without a second copy of the fetch.
 */
export async function spendChoice(handoff: string): Promise<AuthUser> {
  return spendAt("/auth/enter", handoff);
}

async function spendAt(path: string, handoff: string): Promise<AuthUser> {
  const headers: Record<string, string> = { "content-type": "application/json" };
  const slug = clinicSlug();
  if (slug !== undefined) headers[CLINIC_HEADER] = slug;

  const response = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    method: "POST",
    headers,
    body: JSON.stringify({ handoff }),
  });

  if (!response.ok) await fail(response);

  const pair = (await response.json()) as TokenPair;
  tokens.set(pair);
  return pair.user;
}

// ── password reset (STORY-063b) ─────────────────────────────────────────────
//
// **The API has sent these links since 2026-08-31 and the web app had no route
// to receive them**, so a reset email landed on a blank page. Both halves are
// here now: asking for a link, and spending one.

/**
 * Ask for a reset link. **Always resolves**, whatever the address — the server
 * answers 204 for an unknown account on purpose, so that this cannot be used to
 * ask whether somebody works at a practice.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const headers: Record<string, string> = { "content-type": "application/json" };
  const slug = clinicSlug();
  if (slug !== undefined) headers[CLINIC_HEADER] = slug;

  const response = await fetch(`${API_URL}/auth/password-reset`, {
    credentials: "include",
    method: "POST",
    headers,
    body: JSON.stringify({ email }),
  });

  if (!response.ok) await fail(response);
}

/** Spend a reset link. The person then signs in normally. */
export async function completePasswordReset(token: string, password: string): Promise<void> {
  const headers: Record<string, string> = { "content-type": "application/json" };
  const slug = clinicSlug();
  if (slug !== undefined) headers[CLINIC_HEADER] = slug;

  const response = await fetch(`${API_URL}/auth/password-reset/complete`, {
    credentials: "include",
    method: "POST",
    headers,
    body: JSON.stringify({ token, password }),
  });

  if (!response.ok) await fail(response);
}

/**
 * Replace your own password.
 *
 * **Every session ends server-side, including this one**, so the caller signs
 * out afterwards rather than carrying a refresh token that has been revoked.
 */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await api<void>("/auth/password", {
    method: "POST",
    body: { currentPassword, newPassword },
  });
}

/**
 * End the session **in this browser only** — 2026-09-21.
 *
 * ── Why this exists beside `logout` ────────────────────────────────────────
 *
 * `logout` revokes the refresh token at the server as well, which is right
 * when you hold the token you are ending. This is for the other case: a page
 * that has just been *arrived at* after somebody signed out somewhere else,
 * and which finds a session belonging to whoever used this origin last.
 *
 * There is nothing to revoke — that token is not the one that was signed out
 * of, and it may belong to a different person entirely — but it must not be
 * resumed either. So the credential is dropped here and the server is left
 * alone; the token expires on its own schedule, exactly as an abandoned
 * session always has.
 */
export function endSessionLocally(): void {
  tokens.clear();
  activeBranch.set(null);
}

export async function logout(): Promise<void> {
  const refreshToken = tokens.getRefreshToken();

  /**
   * **Cleared here, before the server is told, and the order matters.**
   *
   * The local clear is what actually ends the session in this browser; the
   * POST below is a courtesy that revokes the token at the other end. Doing
   * them the other way round left the credential readable for the length of a
   * round trip — indefinitely, if the network hung — which is invisible on the
   * sign-out button and is the entire point on the accept-invite screen, where
   * signing the previous person out is the reason the call is being made at
   * all.
   */
  tokens.clear();
  activeBranch.set(null);

  if (refreshToken !== null) {
    // Best effort, and deliberately last: a failure here must not trap someone
    // in a session they have asked to leave.
    await fetch(`${API_URL}/auth/logout`, {
      credentials: "include",
      method: "POST",
      headers: { "content-type": "application/json", "x-dentacore-refresh": "1" },
      // The cookie names the session; an old stored token is sent once instead.
      body: JSON.stringify(refreshToken === SESSION_IN_COOKIE ? {} : { refreshToken }),
    }).catch(() => undefined);
  }
}

/**
 * What a cold load found in storage.
 *
 * `switched` is the interesting one: the refresh token is perfectly valid, but
 * it no longer belongs to the person this tab was last acting as. See
 * `SESSION_OWNER_KEY` in `tokens.ts` for how that happens — it is the ordinary
 * consequence of one refresh-token slot per origin and two accounts at one
 * practice, not a fault.
 */
export type RestoredSession =
  { kind: "none" } | { kind: "restored"; user: AuthUser } | { kind: "switched"; user: AuthUser };

/** Re-exported so a `storage` listener can recognise the key by name. */
export { REFRESH_TOKEN_KEY } from "./tokens";

/**
 * Bind this tab to a user it has decided to act as.
 *
 * Called when someone accepts a `switched` session, so the next reload compares
 * against the identity they chose rather than reporting the same switch again.
 */
export function claimSessionForThisTab(userId: string): void {
  tokens.setSessionOwner(userId);
}

/**
 * Drop this tab's claim without ending the session.
 *
 * Used when a tab is about to reload into whoever now holds the stored token:
 * leaving the old binding in place would make that reload report the very
 * switch the person has just accepted.
 */
export function releaseSessionForThisTab(): void {
  tokens.clearSessionOwner();
}

/**
 * Restores a session from the stored refresh token on a cold load.
 *
 * `none` means there is nothing to restore, which the router reads as "show the
 * login screen". **`switched` is not a failure and the session is real** — the
 * caller decides whether to adopt it, and the only thing forbidden is adopting
 * it silently.
 */
export async function restoreSession(): Promise<RestoredSession> {
  if (tokens.getRefreshToken() === null) return { kind: "none" };

  try {
    await coordinatedRefresh();
    const user = await api<AuthUser>("/auth/me");
    // The refresh returned tokens but no user, so nothing has told `tokens` who
    // this is. `activeBranch` reads the clinic id from there to key itself, and
    // a cold reload is exactly when a remembered branch matters most.
    tokens.setUser(user);

    const owner = tokens.getSessionOwner();
    // A tab with no recorded owner has never held a session of its own — a
    // fresh tab, or storage that cannot be written. Nothing to contradict, so
    // it adopts what it found, exactly as it always did.
    if (owner !== null && owner !== user.id) return { kind: "switched", user };

    tokens.setSessionOwner(user.id);
    return { kind: "restored", user };
  } catch {
    tokens.clear();
    return { kind: "none" };
  }
}

/**
 * The signed-in user read fresh — with the permissions they hold **at the
 * active location** (R-D6). Read again whenever that location changes.
 */
export function currentUser(): Promise<AuthUser> {
  return api<AuthUser>("/auth/me");
}
